import { useState, type ReactNode } from "react"
import { DayObject } from "@/types/date"
import {
  LoyaltyFact,
  LoyaltyRedemption,
  LoyaltySource,
  QuoteLoyalty,
} from "@/types/price-quote"
import { cn } from "cn"

import { formatDayObjectAsMonthDay } from "@/lib/format-utils"
import { formatHotelProviderName } from "@/lib/hotel-utils"
import { Locale } from "@/lib/locale-hooks"
import { VERDICT_WORDS } from "@/lib/quote-value-copy"
import { useAppSelector } from "@/lib/storage/hooks"
import { selectIsMindtripUser } from "@/lib/storage/user-slice"
import IconChevronRight from "@/components/icons/chevron-right"

// Every number and the card's sentence are the backend's Loyalty::QuoteFacts,
// computed from the loyalty rules corpus (and recomputed at a live price that
// moved); this file only lays the facts out. Each fact shows its source quote, so a wrong number
// traces straight back to the rule that produced it.

/** The points facts to show, when the traveler cares about points here. */
export function shownLoyalty(
  loyalty: QuoteLoyalty | undefined
): QuoteLoyalty | undefined {
  return loyalty?.interest.length ? loyalty : undefined
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// "TPG_March_2026" → "TPG March 2026"
function sourceName(name: string) {
  return name.replace(/_/g, " ")
}

function sourceHost(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return url
  }
}

function SourceQuote({ source }: { source?: LoyaltySource }) {
  if (!source?.quote && !source?.url) return null
  const text = source.quote?.replace(/\s+/g, " ").trim()
  return (
    <div className="text-2xs text-muted">
      {text && <span className="italic">“{text}”</span>}
      {source.url && (
        <>
          {text && <span> · </span>}
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="no-translate underline"
          >
            {sourceHost(source.url)}
          </a>
        </>
      )}
    </div>
  )
}

/** A conditional fact's condition, or an unknown fact's reason. */
function FactCaveat({ fact }: { fact?: LoyaltyFact }) {
  if (fact?.status === "conditional" && fact.condition) {
    return (
      <div className="text-2xs text-yellow-11">Only if {fact.condition}</div>
    )
  }
  if (fact?.status === "unknown" && fact.reason) {
    return <div className="text-2xs text-muted">Unknown: {fact.reason}</div>
  }
  return null
}

/** One headline number: a tile in the card, a label-and-value row in the narrow popover. */
function Stat({
  label,
  value,
  note,
  flagged,
  compact,
  className,
  children,
}: {
  label: string
  value: string
  note?: string
  /** a conditional fact: the value only holds if its condition does */
  flagged?: boolean
  compact: boolean
  className?: string
  /** shown under the figure, e.g. the redemption scale */
  children?: ReactNode
}) {
  const figure = (
    <span className="no-translate font-semibold">
      {value}
      {flagged && <span className="text-yellow-11">*</span>}
    </span>
  )
  if (compact) {
    return (
      <div className={cn("py-1 text-sm", className)}>
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-muted">{label}</span>
          <span className="text-right">
            {figure}
            {note && <span className="block text-2xs text-muted">{note}</span>}
          </span>
        </div>
        {children}
      </div>
    )
  }
  return (
    <div className={cn("rounded-xl bg-gray-2 px-3 py-2.5", className)}>
      <div className="text-xs text-muted">{label}</div>
      <div className="text-lg leading-tight">{figure}</div>
      {note && <div className="text-2xs text-muted">{note}</div>}
      {children}
    </div>
  )
}

type Verdict = NonNullable<LoyaltyRedemption["verdict"]>

// The words are the quote value meter's (quote-value-copy.ts), so both read the same scale.
const VERDICTS: { verdict: Verdict; fill: string; text: string }[] = [
  { verdict: "poor", fill: cn("bg-red-9"), text: cn("text-red-11") },
  { verdict: "fair", fill: cn("bg-amber-9"), text: cn("text-amber-11") },
  { verdict: "good", fill: cn("bg-green-9"), text: cn("text-green-11") },
  {
    verdict: "exceptional",
    fill: cn("bg-green-11"),
    text: cn("text-green-12"),
  },
]

/**
 * The backend's verdict on a redemption, as a scale from poor to exceptional with this one lit, and each band's
 * redemption values (the backend's too) under it so the call can be checked.
 */
function RedemptionScale({
  redemption,
  compact,
}: {
  redemption: LoyaltyRedemption
  compact: boolean
}) {
  const bands = new Map(
    redemption.verdict_bands?.map((band) => [band.verdict, band])
  )
  const range = (verdict: Verdict) => {
    const band = bands.get(verdict)
    if (!band) return undefined
    if (band.from_cents == null) return `under ${band.to_cents}¢`
    if (band.to_cents == null) return `${band.from_cents}¢+`
    return `${band.from_cents}–${band.to_cents}¢`
  }
  return (
    <div
      className={cn("grid grid-cols-4 gap-1", compact ? "mt-1.5" : "mt-2")}
      role="img"
      aria-label={`Redemption value: ${redemption.verdict} on a scale from poor to exceptional`}
    >
      {VERDICTS.map(({ verdict, fill, text }) => {
        const label = VERDICT_WORDS[verdict]
        const lit = verdict === redemption.verdict
        return (
          <div key={verdict} className="min-w-0">
            <div
              className={cn("h-1.5 rounded-full", lit ? fill : "bg-gray-4")}
            />
            <div
              className={cn(
                "mt-1 truncate text-2xs",
                lit ? `font-semibold ${text}` : "text-muted"
              )}
            >
              {label}
            </div>
            {range(verdict) && (
              <div className="truncate text-2xs text-muted">
                {range(verdict)}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** One fact in the sources list: what we computed, how, and the rule it came from. */
function SourceRow({
  title,
  detail,
  fact,
  children,
}: {
  title: string
  detail?: ReactNode
  fact?: LoyaltyFact
  children?: ReactNode
}) {
  return (
    <li className="space-y-0.5">
      <div className="text-xs font-medium">{title}</div>
      {detail && <div className="text-2xs text-muted">{detail}</div>}
      <SourceQuote source={fact?.source} />
      <FactCaveat fact={fact} />
      {children}
    </li>
  )
}

/** The backend's one sentence about paying with points, plus a program caveat for a conditional brand. */
export function QuotePointsSummary({
  loyalty,
  className,
}: {
  loyalty: QuoteLoyalty
  className?: string
}) {
  const participation =
    loyalty.participation?.status === "conditional"
      ? loyalty.participation.condition
      : undefined
  return (
    // no-translate: the sentence can carry the traveler's tier, which must not go to the translation service
    <div className={cn("no-translate space-y-0.5 text-sm", className)}>
      <p>{loyalty.sentence}</p>
      {participation && <p className="text-xs">{capitalize(participation)}</p>}
    </div>
  )
}

/**
 * The points call at a glance (break-even, their balance, what booking direct
 * earns), the perks and cards behind it, and every fact's source one tap away.
 * `compact` (the price popover) leads with the summary and leaves the perks and
 * cards to the sources list.
 */
export function QuotePointsBreakdown({
  loyalty,
  provider,
  compact = false,
}: {
  loyalty: QuoteLoyalty
  /** the quote's provider key */
  provider: string
  compact?: boolean
}) {
  const locale = Locale.useLocale()
  const formatNumber = Locale.useFormatNumber()
  const formatMoney = Locale.useFormatMoney()
  const isStaff = useAppSelector(selectIsMindtripUser)
  const [showSources, setShowSources] = useState(false)

  const usd = (amount: number) => formatMoney(amount, false, "USD")
  const day = (d: DayObject) => formatDayObjectAsMonthDay(locale, d)
  const { earning_direct: earning, direct_rate: directRate } = loyalty
  const { tier, balance } = loyalty.personal
  const redemption =
    loyalty.redemption?.status !== "unknown" ? loyalty.redemption : undefined
  const earned =
    earning && earning.status !== "unknown" && earning.points != null
      ? earning
      : undefined
  const bonus =
    earned?.elite_bonus?.status !== "unknown" ? earned?.elite_bonus : undefined
  const cards = loyalty.card_earning.filter(
    (card) => card.status !== "unknown" && card.points != null
  )
  const providerName = formatHotelProviderName(provider) || "this provider"
  // Cash against what the points are worth only answers "cash or points?"; someone paying with points sees the award
  // against their balance instead.
  const payingWithPoints =
    redemption != null &&
    (loyalty.flavor === "covers" || loyalty.flavor === "points_price")
  const benchmarkView =
    redemption?.redemption_cents != null &&
    (loyalty.flavor == null || loyalty.flavor === "redemption_benchmark")
  const stayPoints = redemption?.points_for_stay ?? 0
  const portal = loyalty.portal
  const portalOption = portal?.options[0]
  // With no points in the program itself, the bank transfer that reaches furthest into it.
  const reachTransfer = loyalty.transfers
    .filter((transfer) => transfer.reachable_points != null)
    .sort((a, b) => (b.reachable_points ?? 0) - (a.reachable_points ?? 0))[0]
  // Unknown facts the tiles leave out, so what we couldn't confirm still shows.
  const unknowns = [
    earning?.status === "unknown" ? earning : null,
    ...loyalty.card_earning.filter((card) => card.status === "unknown"),
  ].filter((fact): fact is LoyaltyFact => fact != null)

  return (
    // no-translate: tier, balance and personal conditions must not go to the translation service
    <div className="no-translate space-y-3">
      {compact && <QuotePointsSummary loyalty={loyalty} />}

      <div
        className={cn(
          compact
            ? "divide-y"
            : "grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-2"
        )}
      >
        {payingWithPoints && redemption && (
          <>
            <Stat
              compact={compact}
              label="Points"
              value={`${formatNumber(Math.round(redemption.award_points_per_night ?? 0))}/night`}
              note={`${formatNumber(stayPoints)} for the stay`}
              flagged={redemption.status === "conditional"}
            />
            {balance && (
              <Stat
                compact={compact}
                label="Your points"
                value={formatNumber(balance.points)}
                note={
                  balance.points >= stayPoints
                    ? `covers it, leaving ${formatNumber(balance.points - stayPoints)}`
                    : `${formatNumber(stayPoints - balance.points)} short`
                }
              />
            )}
          </>
        )}
        {benchmarkView && (
          <>
            <Stat
              compact={compact}
              label="Cash"
              value={`${usd(redemption.cash_per_night ?? 0)}/night`}
              note={`${redemption.against === "all_in" ? "all-in" : "before taxes"} via ${providerName}`}
            />
            <Stat
              compact={compact}
              label="Points"
              value={`${formatNumber(redemption.points_for_stay ?? 0)} points`}
              note={`worth ~${usd(redemption.points_worth_per_night ?? 0)}/night at the typical ${redemption.cents_per_point}¢`}
            />
            <Stat
              compact={compact}
              label="Redemption value"
              value={`${redemption.redemption_cents}¢ a point`}
              flagged={redemption.status === "conditional"}
              className="col-span-full"
            >
              <RedemptionScale redemption={redemption} compact={compact} />
            </Stat>
          </>
        )}
        {payingWithPoints && !balance && reachTransfer && (
          <Stat
            compact={compact}
            label="Your bank points"
            value={formatNumber(reachTransfer.balance ?? 0)}
            note={`${reachTransfer.from}: become ${formatNumber(reachTransfer.reachable_points ?? 0)}${reachTransfer.short_by != null ? `, ${formatNumber(reachTransfer.short_by)} short` : ", enough for the stay"}${reachTransfer.condition ? ` (only if ${reachTransfer.condition})` : ""}`}
            flagged={reachTransfer.status === "conditional"}
          />
        )}
        {loyalty.flavor === "portal" && portal && portalOption && (
          <>
            <Stat
              compact={compact}
              label="Card points"
              value={`≈${formatNumber(portalOption.points)}`}
              note={`${portalOption.from} at ${portalOption.cents_per_point}¢ on ${usd(portal.total_usd)}${portal.against === "all_in" ? " all-in" : " before taxes"}`}
              flagged
            />
            {portalOption.balance != null && (
              <Stat
                compact={compact}
                label="Your points"
                value={formatNumber(portalOption.balance)}
                note={
                  portalOption.gap_usd
                    ? `cover ${usd(portalOption.covers_usd ?? 0)}, ${usd(portalOption.gap_usd)} in cash`
                    : "cover it"
                }
              />
            )}
          </>
        )}
        {!benchmarkView &&
          !payingWithPoints &&
          directRate?.cash_per_night != null && (
            <Stat
              compact={compact}
              label="Member rate, booked direct"
              value={`${usd(directRate.cash_per_night)}/night`}
              note={`before taxes${directRate.non_member_cash_per_night != null ? `; non-member ${usd(directRate.non_member_cash_per_night)}` : ""}`}
            />
          )}
        {!redemption && balance && (
          <Stat
            compact={compact}
            label="Your points"
            value={formatNumber(balance.points)}
            note={
              balance.last_observed
                ? `as of ${day(balance.last_observed)}`
                : undefined
            }
          />
        )}
        {!benchmarkView && !payingWithPoints && earned?.points != null && (
          <Stat
            compact={compact}
            label="Booking direct earns"
            value={`~${formatNumber(earned.points)}`}
            note={[
              earned.worth_usd != null ? `≈ ${usd(earned.worth_usd)}` : null,
              bonus?.percent != null
                ? `with ${tier?.name ?? "elite"} +${bonus.percent}%`
                : null,
            ]
              .filter(Boolean)
              .join(", ")}
            flagged={
              earned.status === "conditional" ||
              earned.elite_bonus?.status === "conditional"
            }
          />
        )}
      </div>
      {benchmarkView && (balance || earned?.points != null) && (
        <p className="text-xs text-muted">
          {`${capitalize(
            [
              balance
                ? `you have ${formatNumber(balance.points)} points`
                : null,
              directRate?.cash_per_night != null
                ? `the member rate booked direct is ${usd(directRate.cash_per_night)}/night before taxes`
                : null,
              earned?.points != null
                ? `booking direct earns ~${formatNumber(earned.points)} points`
                : null,
            ]
              .filter(Boolean)
              .join("; ")
          )}.`}
        </p>
      )}

      {!compact && loyalty.status_perks.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs text-muted">
            {`${tier ? `${tier.name} perks` : "Status perks"} when booked direct`}
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {loyalty.status_perks.map((perk) => (
              <li
                key={perk.benefit}
                className="rounded-full bg-gray-3 px-2.5 py-0.5 text-xs"
              >
                {capitalize(perk.benefit)}
                {perk.status === "conditional" && (
                  <span className="text-yellow-11">*</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!compact &&
        !payingWithPoints &&
        cards.map((card) => (
          <p key={card.card} className="text-xs text-muted">
            {`Paid with your ${card.card}: ~${formatNumber(card.points ?? 0)} ${card.currency}, however you book${card.condition ? ` (only if ${card.condition})` : ""}`}
          </p>
        ))}
      {/* Paying with points and short, the sentence already gives the bank points that close the gap. */}
      {!compact &&
        loyalty.flavor !== "covers" &&
        loyalty.transfers.map((transfer) => (
          <p key={transfer.from} className="text-xs text-muted">
            {transfer.short_by != null && transfer.balance != null
              ? `Your ${formatNumber(transfer.balance)} ${transfer.from} points move over as ${formatNumber(transfer.reachable_points ?? 0)}${transfer.condition ? ` (only if ${transfer.condition})` : ""}, ${formatNumber(transfer.short_by)} short of the award. Transfers can’t be undone: confirm the award first.`
              : `${transfer.from} covers the award with ${transfer.lower_bound ? "at least " : ""}${formatNumber(transfer.points_to_cover_award ?? 0)}${transfer.condition ? ` (only if ${transfer.condition})` : ""}. Transfers can’t be undone: confirm the award first.`}
          </p>
        ))}

      <div>
        <button
          type="button"
          onClick={() => setShowSources(!showSources)}
          aria-expanded={showSources}
          className="flex items-center gap-1 text-xs font-medium text-muted hover:text-foreground"
        >
          <IconChevronRight
            className={cn("transition-transform", showSources && "rotate-90")}
          />
          How we got these numbers
        </button>
        {showSources && (
          <ul
            className={cn(
              "mt-2 space-y-2.5 border-l-2 pl-3",
              compact && "max-h-64 overflow-y-auto"
            )}
          >
            {redemption?.redemption_cents != null && (
              <SourceRow
                title={`Redemption value: ${redemption.redemption_cents}¢ a point`}
                fact={redemption}
                detail={`${usd(redemption.cash_per_night ?? 0)}${redemption.against === "all_in" ? " all-in" : " before taxes"}${redemption.at_live_price ? " (live price)" : ""} a night ÷ ${formatNumber(Math.round(redemption.award_points_per_night ?? 0))} points a night (the award, from ${redemption.award_source}). The points are worth ~${usd(redemption.points_worth_per_night ?? 0)} a night at ${redemption.cents_per_point}¢; ${loyalty.program} points typically get ${redemption.typical_cents?.join("–")}¢: ${redemption.meaning}.${
                  redemption.value_sources?.length
                    ? ` Point value: ${redemption.value_sources.map((v) => `${sourceName(v.name)} ${v.cents}¢`).join(", ")}.`
                    : ""
                }${redemption.fee ? ` The points rate still charges ${redemption.fee} in cash.` : ""}`}
              />
            )}
            {directRate?.cash_per_night != null && (
              <SourceRow
                title={`Member rate: ${usd(directRate.cash_per_night)}/night before taxes`}
                fact={directRate}
                detail={`${directRate.rate_name ?? "Member rate"}, ${usd(directRate.cash_total ?? 0)} for the stay${directRate.non_member_cash_per_night != null ? `; non-member ${usd(directRate.non_member_cash_per_night)}/night` : ""}, from ${directRate.read_from}.`}
              >
                {directRate.quote_url && (
                  <a
                    href={directRate.quote_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-2xs underline"
                  >
                    Book direct
                  </a>
                )}
              </SourceRow>
            )}
            {earned && (
              <SourceRow
                title={`Booking direct: ~${formatNumber(earned.points ?? 0)} points`}
                fact={earned}
                detail={`${earned.points_per_dollar} per dollar on the ${usd(earned.on_base_spend ?? 0)} room rate${bonus?.percent != null ? `, plus the ${bonus.percent}% ${tier?.name ?? "elite"} bonus` : ""}`}
              >
                {bonus && <SourceQuote source={bonus.source} />}
                <FactCaveat fact={earned.elite_bonus} />
              </SourceRow>
            )}
            {cards.map((card) => (
              <SourceRow
                key={card.card}
                title={`${card.card}: ~${formatNumber(card.points ?? 0)}`}
                fact={card}
                detail={`${card.points_per_dollar}x ${card.currency} on the ${card.spend_basis}`}
              />
            ))}
            {loyalty.transfers.map((transfer) => (
              <SourceRow
                key={transfer.from}
                title={`${transfer.from} → ${loyalty.program}`}
                fact={transfer}
                detail={`${transfer.bonus_ratio ?? transfer.ratio}:1${transfer.bonus_ends ? `, bonus until ${day(transfer.bonus_ends)}` : ""}`}
              />
            ))}
            {portal &&
              portal.options.map((option) => (
                <SourceRow
                  key={option.from}
                  title={`${option.from} in the travel portal: ${option.cents_per_point}¢ a point`}
                  fact={{
                    status: portal.status,
                    condition: portal.condition,
                    source: option.source,
                  }}
                  detail={`${usd(portal.total_usd)}${portal.against === "all_in" ? " all-in" : " before taxes"} at this quote's price ÷ ${option.cents_per_point}¢ = ~${formatNumber(option.points)} points.`}
                />
              ))}
            {loyalty.status_perks.map((perk) => (
              <SourceRow
                key={perk.benefit}
                title={capitalize(perk.benefit)}
                fact={perk}
              >
                {isStaff && perk.value && (
                  <div className="no-translate text-2xs text-muted">
                    Read as: {perk.value}
                  </div>
                )}
              </SourceRow>
            ))}
            {unknowns.map((fact) => (
              <li key={fact.reason}>
                <FactCaveat fact={fact} />
              </li>
            ))}
            <li className="text-2xs text-muted">
              {`${loyalty.portal_only ? "" : `Hotel points and perks need a direct rate that earns, which a ${providerName} booking usually isn’t; card points apply either way. `}Computed ${day(loyalty.computed_on)} from ${loyalty.program}${loyalty.program.endsWith("s") ? "’" : "’s"} published rules${loyalty.rules_as_of ? ` as of ${day(loyalty.rules_as_of)}` : ""}.`}
            </li>
          </ul>
        )}
      </div>
    </div>
  )
}
